import { useState } from "react";
import { useLingui } from "@lingui/react";
import { Loader2, Moon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePresentToday } from "@/hooks/usePresentToday";
import type { AttendanceBucket, AttendanceChild } from "@/types/attendance";
import { formatOrgTime } from "@/utils/dateUtils";

export function PresentTodayBoard() {
  const { i18n } = useLingui();
  const query = usePresentToday();
  const [bucket, setBucket] = useState<AttendanceBucket>("present");
  const labels: Record<AttendanceBucket, string> = {
    present: i18n._({
      id: "activities.presentToday.present",
      message: "Present now",
    }),
    expected: i18n._({
      id: "activities.presentToday.expected",
      message: "Expected",
    }),
    checkedOut: i18n._({
      id: "activities.presentToday.checkedOut",
      message: "Checked out",
    }),
    absent: i18n._({ id: "activities.presentToday.absent", message: "Absent" }),
  };
  // A failed background refresh must not leave old totals looking authoritative.
  if (query.isError)
    return (
      <div
        role="alert"
        className="space-y-3 rounded-md border border-destructive p-4"
      >
        <p>
          {i18n._({
            id: "activities.presentToday.error",
            message: "Attendance could not be loaded. Please retry.",
          })}
        </p>
        <Button onClick={() => query.refetch()}>
          {i18n._({ id: "common.retry", message: "Try again" })}
        </Button>
      </div>
    );
  if (!query.data)
    return (
      <div
        role="status"
        className="flex items-center justify-center gap-2 py-12"
      >
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        {i18n._({ id: "common.loading.data", message: "Loading data..." })}
      </div>
    );

  const { groups, childcareNames } = query.data;
  const count = (children: AttendanceChild[], status: AttendanceBucket) =>
    new Set(
      children
        .filter((child) => child.bucket === status)
        .map((child) => child.id),
    ).size;
  const allChildren = groups.flatMap((group) => group.children);
  const childcareIds = [...new Set(groups.map((group) => group.childcareId))];
  const presentLabel = (total: number) =>
    i18n._({
      id: "activities.presentToday.presentCount",
      message: "{count, plural, one {# present} other {# present}}",
      values: { count: total },
    });
  const unassignedLabel = i18n._({
    id: "activities.presentToday.unassigned",
    message: "Group needs clarification",
  });

  return (
    <div className="space-y-6 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">
            {presentLabel(count(allChildren, "present"))}
          </h2>
          <p className="text-sm text-muted-foreground">
            {query.date} /{" "}
            {i18n._({
              id: "activities.presentToday.updated",
              message: "Updated {time}",
              values: {
                time: formatOrgTime(
                  new Date(query.dataUpdatedAt).toISOString(),
                ),
              },
            })}
          </p>
        </div>
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label={i18n._({
            id: "activities.presentToday.status",
            message: "Attendance status",
          })}
        >
          {(Object.keys(labels) as AttendanceBucket[]).map((status) => (
            <Button
              key={status}
              variant={bucket === status ? "default" : "outline"}
              size="sm"
              aria-pressed={bucket === status}
              onClick={() => setBucket(status)}
            >
              {labels[status]} ({count(allChildren, status)})
            </Button>
          ))}
        </div>
      </div>
      {groups.length === 0 && (
        <p className="text-muted-foreground">
          {i18n._({
            id: "activities.presentToday.noGroups",
            message: "No groups configured",
          })}
        </p>
      )}
      {childcareIds.map((childcareId) => {
        const childcareGroups = groups.filter(
          (group) => group.childcareId === childcareId,
        );
        return (
          <section key={childcareId} className="space-y-3">
            <div className="flex items-center justify-between gap-3 border-b pb-2">
              <h3 className="text-lg font-semibold">
                {childcareNames[childcareId] || childcareId || unassignedLabel}
              </h3>
              <Badge variant="secondary">
                {presentLabel(
                  count(
                    childcareGroups.flatMap((group) => group.children),
                    "present",
                  ),
                )}
              </Badge>
            </div>
            <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
              {childcareGroups.map((group) => {
                const visible = group.children
                  .filter((child) => child.bucket === bucket)
                  .sort((a, b) => a.name.localeCompare(b.name, i18n.locale));
                return (
                  <Card key={group.id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between gap-2 text-base">
                        <span>{group.name || unassignedLabel}</span>
                        <Badge variant="secondary">
                          {presentLabel(count(group.children, "present"))}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {visible.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          {i18n._({
                            id: "activities.presentToday.noneInStatus",
                            message: "No children with this status",
                          })}
                        </p>
                      ) : (
                        <ul className="divide-y">
                          {visible.map((child) => (
                            <li
                              key={child.id}
                              className="flex items-center justify-between gap-3 py-2 text-sm"
                            >
                              <span className="min-w-0 break-words">
                                {child.name}
                              </span>
                              <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                                {child.bucket === "present" && (
                                  <>
                                    {formatOrgTime(child.state?.check_in_time)}
                                    {child.state?.is_napping && (
                                      <Badge variant="secondary">
                                        <Moon
                                          className="mr-1 h-3 w-3"
                                          aria-hidden="true"
                                        />
                                        {i18n._({
                                          id: "activities.presentToday.napping",
                                          message: "Napping",
                                        })}
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
